
<div class="totp-setup">
  <!-- Step 1: QR Code Scan & Verify -->
  <div v-if="step === 'scan'" class="totp-setup__step">
    <div class="totp-setup__header">
      <p class="totp-setup__subtitle">
        Scan the QR code below with your authenticator app
      </p>
    </div>

    <!-- QR Code Display -->
    <div class="totp-setup__qr-container">
      <div class="totp-setup__qr-wrapper">
        <qrcode-vue
          v-if="uri"
          :value="uri"
          :size="200"
          level="M"
          class="totp-setup__qr"
        />
      </div>
    </div>

    <!-- Manual Entry Toggle -->
    <div class="totp-setup__manual-toggle">
      <button
        type="button"
        class="totp-setup__toggle-btn"
        @click="toggleManualEntry"
      >
        {{ showManualEntry ? 'Hide manual entry' : "Can't scan? Enter manually" }}
      </button>
    </div>

    <!-- Manual Entry Section -->
    <transition name="slide-fade">
      <div v-if="showManualEntry" class="totp-setup__manual-section">
        <div class="totp-setup__manual-field">
          <p class="totp-setup__manual-label">Account name</p>
          <p class="totp-setup__manual-value">{{ email }}</p>
        </div>
        <div class="totp-setup__manual-field">
          <p class="totp-setup__manual-label">Your secret key</p>
          <div class="totp-setup__secret-row">
            <code class="totp-setup__secret-code">{{ secret }}</code>
            <v-btn
              icon
              small
              class="totp-setup__copy-btn"
              :aria-label="copied ? 'Copied!' : 'Copy secret'"
              @click="handleCopySecret"
            >
              <v-icon :size="16">
                {{ copied ? 'mdi-check-circle' : 'mdi-content-copy' }}
              </v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </transition>

    <!-- Verification Input -->
    <div class="totp-setup__verification">
      <label for="verification-code" class="totp-setup__input-label">
        Verification Code
      </label>
      <input
        id="verification-code"
        v-model="verificationCode"
        type="text"
        inputmode="numeric"
        placeholder="000000"
        maxlength="6"
        :disabled="isVerifying"
        class="totp-setup__input"
        autocomplete="off"
        @input="handleCodeInput"
        @keypress.enter="handleVerify"
      />
      <p class="totp-setup__input-hint">
        Enter the 6-digit code from your authenticator app
      </p>
      <transition name="fade">
        <p v-if="error" class="totp-setup__error">{{ error }}</p>
      </transition>
    </div>

    <!-- Action Buttons -->
    <div class="totp-setup__actions">
      <v-btn
        outlined
        large
        class="totp-setup__btn totp-setup__btn--cancel"
        :disabled="isVerifying"
        @click="handleCancel"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        large
        class="totp-setup__btn totp-setup__btn--continue"
        :disabled="isVerifying || verificationCode.length !== 6"
        :loading="isVerifying"
        @click="handleVerify"
      >
        {{ isVerifying ? 'Verifying...' : 'Continue' }}
      </v-btn>
    </div>
  </div>

  <!-- Step 2: Backup Codes -->
  <div v-if="step === 'backup-codes'" class="totp-setup__step">
    <div class="totp-setup__header">
      <div class="totp-setup__icon-wrapper">
        <v-icon color="primary" :size="32">mdi-shield-check</v-icon>
      </div>
      <h2 class="totp-setup__title">Save Your Backup Codes</h2>
      <p class="totp-setup__subtitle">
        If you lose access to your authenticator app, you can use these codes to sign in.
        Each code can only be used once.
      </p>
    </div>

    <!-- Backup Codes Grid -->
    <div class="totp-setup__codes-container">
      <div class="totp-setup__codes-grid">
        <div
          v-for="(code, index) in backupCodes"
          :key="index"
          class="totp-setup__code-item"
        >
          {{ code }}
        </div>
      </div>
    </div>

    <!-- Copy/Download Actions -->
    <div class="totp-setup__code-actions">
      <v-btn
        outlined
        large
        class="totp-setup__btn totp-setup__btn--code-action"
        @click="handleCopyBackupCodes"
      >
        <v-icon left :size="16">
          {{ backupCodesCopied ? 'mdi-check-circle' : 'mdi-content-copy' }}
        </v-icon>
        {{ backupCodesCopied ? 'Copied!' : 'Copy Codes' }}
      </v-btn>
      <v-btn
        outlined
        large
        class="totp-setup__btn totp-setup__btn--code-action"
        @click="handleDownloadBackupCodes"
      >
        <v-icon left :size="16">mdi-download</v-icon>
        Download
      </v-btn>
    </div>

    <!-- Acknowledgement Checkbox -->
    <div class="totp-setup__acknowledgement">
      <v-checkbox
        v-model="acknowledgedSaved"
        class="totp-setup__checkbox"
        color="warning"
      >
        <template #label>
          <span class="totp-setup__acknowledgement-text">
            I have saved these backup codes in a secure location. I understand that without
            these codes, I may lose access to my account if I lose my authenticator device.
          </span>
        </template>
      </v-checkbox>
    </div>

    <!-- Complete Button -->
    <v-btn
      color="primary"
      large
      block
      class="totp-setup__btn totp-setup__btn--complete"
      :disabled="!acknowledgedSaved"
      @click="handleCompleteSetup"
    >
      Complete Setup
    </v-btn>
  </div>
</div>
